import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
  IconButton,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  QrCodeScanner,
  CheckCircle,
  Cancel,
  Logout as LogoutIcon,
  Login as LoginIcon,
  CameraAlt,
} from "@mui/icons-material";
import { Html5Qrcode } from "html5-qrcode";
import {
  getCurrentMinuteSlot,
  checkInBallkid,
  checkOutBallkid,
  getCheckInStatusDetailed,
  getAvailableBallkidsForDay,
  getBallkidNameById,
  invalidateCheckInCache,
} from "../firebaseService";
import { useGroup } from "../context/GroupContext";
import { useTournamentConfig } from "../context/TournamentConfigContext";
import { useDefaultTournamentDay } from "../utils/useDefaultTournamentDay";

const formatTime = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const CheckInScanner = () => {
  const { activeGroup } = useGroup();
  const { daysOfWeek } = useTournamentConfig();
  const [selectedDay] = useDefaultTournamentDay(daysOfWeek);
  const memberLabel = activeGroup?.memberLabel || "Ballkinder";

  const [scanning, setScanning] = useState(false);
  const [availableBallkids, setAvailableBallkids] = useState([]);
  const [checkInStatus, setCheckInStatus] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [lastScan, setLastScan] = useState(null);
  const [scanResult, setScanResult] = useState(null);

  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const scanResultTimeout = useRef(null);

  // Refs for latest state (needed because html5-qrcode callback captures stale closures)
  const checkInStatusRef = useRef(checkInStatus);
  checkInStatusRef.current = checkInStatus;
  const lastScanRef = useRef(lastScan);
  lastScanRef.current = lastScan;
  const selectedDayRef = useRef(selectedDay);
  selectedDayRef.current = selectedDay;

  const loadStatus = useCallback(async () => {
    if (!selectedDay) return;
    const [available, status] = await Promise.all([
      getAvailableBallkidsForDay(selectedDay),
      getCheckInStatusDetailed(selectedDay),
    ]);
    setAvailableBallkids(available);
    setCheckInStatus(status);
  }, [selectedDay]);

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 10000);
    return () => clearInterval(interval);
  }, [loadStatus]);

  const showScanResult = (name, action) => {
    if (scanResultTimeout.current) clearTimeout(scanResultTimeout.current);
    setScanResult({ name, action });
    scanResultTimeout.current = setTimeout(() => setScanResult(null), 2500);
  };

  // Handle scanned QR code
  const handleScan = useCallback(async (decodedText) => {
    const day = selectedDayRef.current;
    if (!day) return;

    try {
      // Decode base64-encoded payload (format: "BK:base64data")
      let jsonStr = decodedText;
      if (decodedText.startsWith("BK:")) {
        jsonStr = atob(decodedText.slice(3));
      }
      const data = JSON.parse(jsonStr);
      const { id, ts } = data;
      if (!id) return;

      // Check timestamp freshness (max 2 minutes old)
      if (ts) {
        const currentSlot = getCurrentMinuteSlot();
        if (Math.abs(currentSlot - ts) > 2) {
          setSnackbar({ open: true, message: "QR-Code abgelaufen!", severity: "warning" });
          return;
        }
      }

      // Prevent duplicate scans within 3 seconds
      const prev = lastScanRef.current;
      if (prev && prev.id === id && Date.now() - prev.time < 3000) return;
      setLastScan({ id, time: Date.now() });
      lastScanRef.current = { id, time: Date.now() };

      // Check if this is a known ballkid
      const name = await getBallkidNameById(id);
      if (!name) {
        setSnackbar({ open: true, message: "Unbekanntes Ballkind!", severity: "error" });
        return;
      }

      // Toggle check-in/out
      const currentStatus = checkInStatusRef.current[id];
      if (currentStatus?.checkedIn && !currentStatus?.checkedOut) {
        await checkOutBallkid(id, day);
        showScanResult(name, "out");
      } else {
        await checkInBallkid(id, day);
        showScanResult(name, "in");
      }

      invalidateCheckInCache();
      loadStatus();
    } catch (e) {
      // Ignore non-JSON QR codes
    }
  }, [loadStatus]);

  // Start/Stop scanner
  const handleScanRef = useRef(handleScan);
  handleScanRef.current = handleScan;

  const startScanner = async () => {
    if (!scannerRef.current) return;
    try {
      const qr = new Html5Qrcode("qr-reader");
      html5QrRef.current = qr;
      await qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => handleScanRef.current(decodedText),
        () => {}
      );
      setScanning(true);
    } catch (err) {
      setSnackbar({ open: true, message: "Kamera-Zugriff fehlgeschlagen", severity: "error" });
    }
  };

  const stopScanner = async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop(); } catch (e) {}
      html5QrRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  const handleCheckOut = async (ballkidId, name) => {
    await checkOutBallkid(ballkidId, selectedDay);
    invalidateCheckInCache();
    showScanResult(name, "out");
    await loadStatus();
  };

  // Stats
  const checkedInCount = Object.values(checkInStatus).filter(
    (s) => s.checkedIn && !s.checkedOut
  ).length;
  const checkedOutCount = Object.values(checkInStatus).filter(
    (s) => s.checkedIn && s.checkedOut
  ).length;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
        Check-In Scanner
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {selectedDay} — Scanne die QR-Codes der {memberLabel}
      </Typography>

      {/* Scanner Area with Overlay */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">
            <QrCodeScanner sx={{ verticalAlign: "middle", mr: 1 }} />
            QR-Scanner
          </Typography>
          <Button
            variant={scanning ? "outlined" : "contained"}
            color={scanning ? "error" : "primary"}
            startIcon={scanning ? <Cancel /> : <CameraAlt />}
            onClick={scanning ? stopScanner : startScanner}
          >
            {scanning ? "Stoppen" : "Scanner starten"}
          </Button>
        </Box>

        {/* Camera + Overlay Container */}
        <Box sx={{ position: "relative", maxWidth: 400, mx: "auto" }}>
          <Box
            id="qr-reader"
            ref={scannerRef}
            sx={{
              width: "100%",
              minHeight: scanning ? 300 : 0,
              borderRadius: 2,
              overflow: "hidden",
            }}
          />

          {/* Scan Result Overlay */}
          {scanResult && scanning && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: scanResult.action === "in"
                  ? "rgba(76, 175, 80, 0.85)"
                  : "rgba(255, 152, 0, 0.85)",
                borderRadius: 2,
                zIndex: 10,
                pointerEvents: "none",
                animation: "fadeInOut 2.5s ease-in-out",
                "@keyframes fadeInOut": {
                  "0%": { opacity: 0 },
                  "10%": { opacity: 1 },
                  "75%": { opacity: 1 },
                  "100%": { opacity: 0 },
                },
              }}
            >
              <CheckCircle sx={{ fontSize: 64, color: "white", mb: 1 }} />
              <Typography variant="h5" sx={{ color: "white", fontWeight: 800, textAlign: "center", px: 2 }}>
                {scanResult.name}
              </Typography>
              <Typography variant="h6" sx={{ color: "white", fontWeight: 600, mt: 0.5 }}>
                {scanResult.action === "in" ? "Eingecheckt" : "Ausgecheckt"}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Non-overlay result (shown when scanner not active) */}
        {scanResult && !scanning && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: scanResult.action === "in" ? "rgba(76,175,80,0.15)" : "rgba(255,152,0,0.15)",
              border: scanResult.action === "in" ? "2px solid #4caf50" : "2px solid #ff9800",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CheckCircle sx={{ fontSize: 40, color: scanResult.action === "in" ? "#4caf50" : "#ff9800" }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{scanResult.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {scanResult.action === "in" ? "Eingecheckt" : "Ausgecheckt"}
              </Typography>
            </Box>
          </Box>
        )}

        {!scanning && !scanResult && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            Klicke "Scanner starten" um die Kamera zu oeffnen.
          </Typography>
        )}
      </Paper>

      {/* Manual Check-In */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Manueller Check-In (Fallback)
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Autocomplete
            options={availableBallkids}
            getOptionLabel={(opt) => opt.name}
            sx={{ flex: 1 }}
            renderInput={(params) => <TextField {...params} label="Ballkind suchen..." size="small" />}
            onChange={async (_, bk) => {
              if (!bk || !selectedDay) return;
              const status = checkInStatus[bk.id];
              if (status?.checkedIn && !status?.checkedOut) {
                await checkOutBallkid(bk.id, selectedDay);
                showScanResult(bk.name, "out");
              } else {
                await checkInBallkid(bk.id, selectedDay);
                showScanResult(bk.name, "in");
              }
              invalidateCheckInCache();
              loadStatus();
            }}
            value={null}
            blurOnSelect
          />
        </Box>
      </Paper>

      {/* Stats */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Chip
          icon={<CheckCircle />}
          label={`${checkedInCount} eingecheckt`}
          color="success"
          sx={{ fontWeight: 600, fontSize: "0.9rem", py: 2.5, px: 1 }}
        />
        <Chip
          icon={<LogoutIcon />}
          label={`${checkedOutCount} ausgecheckt`}
          color="default"
          sx={{ fontWeight: 600, fontSize: "0.9rem", py: 2.5, px: 1 }}
        />
        <Chip
          label={`${availableBallkids.length} angemeldet`}
          color="primary"
          variant="outlined"
          sx={{ fontWeight: 600, fontSize: "0.9rem", py: 2.5, px: 1 }}
        />
      </Box>

      {/* Check-In Status Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Check-In</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Check-Out</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableBallkids.map((bk) => {
                const status = checkInStatus[bk.id];
                const isIn = status?.checkedIn && !status?.checkedOut;
                const isOut = status?.checkedIn && status?.checkedOut;
                return (
                  <TableRow key={bk.id} sx={{ bgcolor: isIn ? "rgba(76,175,80,0.08)" : "transparent" }}>
                    <TableCell>{bk.name}</TableCell>
                    <TableCell align="center">
                      {isOut ? (
                        <Chip label="Fertig" size="small" variant="outlined" />
                      ) : isIn ? (
                        <Chip label="Anwesend" size="small" color="success" />
                      ) : (
                        <Chip label="Ausstehend" size="small" variant="outlined" color="warning" />
                      )}
                    </TableCell>
                    <TableCell align="center">{formatTime(status?.checkIn)}</TableCell>
                    <TableCell align="center">{formatTime(status?.checkOut)}</TableCell>
                    <TableCell align="center">
                      {isIn && (
                        <IconButton
                          size="small"
                          color="warning"
                          onClick={() => handleCheckOut(bk.id, bk.name)}
                          title="Auschecken"
                        >
                          <LogoutIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CheckInScanner;
